import React from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import PushNotificationSettings from "./PushNotificationSettings";
import { getAuthToken } from "../lib/auth";
import {
  isAndroidPlayStoreApp,
  isIOSAppStoreApp,
  isNativeStoreApp,
} from "../lib/appMode";

function getSignedIn() {
  return Boolean(getAuthToken());
}

export default function AppSettingsLauncher() {
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const [tab, setTab] = React.useState("notifications");
  const [signedIn, setSignedIn] = React.useState(getSignedIn);

  React.useEffect(() => {
    const body = document.body;
    const native = isNativeStoreApp();

    body.classList.toggle("ga-native-store-app", native);
    body.classList.toggle("ga-ios-store-app", isIOSAppStoreApp());
    body.classList.toggle("ga-android-store-app", isAndroidPlayStoreApp());

    return () => {
      body.classList.remove("ga-native-store-app");
      body.classList.remove("ga-ios-store-app");
    };
  }, []);

  React.useEffect(() => {
    const refresh = () => setSignedIn(getSignedIn());
    window.addEventListener("auth:changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("auth:changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  React.useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!isNativeStoreApp() || !signedIn) return null;

  const path = String(location.pathname || "");
  const hideLauncher =
    path.startsWith("/stream") ||
    path.startsWith("/obs/") ||
    path.startsWith("/overlay/") ||
    path.startsWith("/popout");

  const go = (to) => {
    setOpen(false);
    nav(to);
  };

  return (
    <>
      {!hideLauncher ? (
        <button
          type="button"
          className="ga-app-settings-launcher"
          onClick={() => {
            setTab("notifications");
            setOpen(true);
          }}
          aria-label="Open app settings"
          title="App settings"
        >
          <span aria-hidden="true">⚙️</span>
          <span className="ga-app-settings-label">Settings</span>
        </button>
      ) : null}

      {open && typeof document !== "undefined"
        ? createPortal(
            <div
              className="ga-settings-backdrop"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) setOpen(false);
              }}
            >
              <section
                className="ga-settings-sheet"
                role="dialog"
                aria-modal="true"
                aria-label="GAStreaming app settings"
              >
                <header className="ga-settings-header">
                  <div>
                    <div className="ga-settings-eyebrow">GAStreaming</div>
                    <h2>App Settings</h2>
                  </div>
                  <button
                    type="button"
                    className="ga-settings-close"
                    onClick={() => setOpen(false)}
                    aria-label="Close settings"
                  >
                    ×
                  </button>
                </header>

                <div className="ga-settings-tabs" role="tablist" aria-label="Settings sections">
                  <button
                    type="button"
                    className={tab === "notifications" ? "is-active" : ""}
                    onClick={() => setTab("notifications")}
                    role="tab"
                    aria-selected={tab === "notifications"}
                  >
                    Notifications
                  </button>
                  <button
                    type="button"
                    className={tab === "account" ? "is-active" : ""}
                    onClick={() => setTab("account")}
                    role="tab"
                    aria-selected={tab === "account"}
                  >
                    Account
                  </button>
                </div>

                <div className="ga-settings-content">
                  {tab === "notifications" ? (
                    <PushNotificationSettings />
                  ) : (
                    <div className="ga-settings-account-list">
                      <button type="button" onClick={() => go("/me")}>
                        <span>
                          <strong>Member profile</strong>
                          <small>Edit your name, photo, bio, badges, and uploads.</small>
                        </span>
                        <b>›</b>
                      </button>
                      <button type="button" onClick={() => go("/privacy")}>
                        <span>
                          <strong>Privacy policy</strong>
                          <small>Review how GAStreaming handles account information.</small>
                        </span>
                        <b>›</b>
                      </button>
                      <button type="button" onClick={() => go("/terms")}>
                        <span>
                          <strong>Terms of service</strong>
                          <small>Read the platform rules and account terms.</small>
                        </span>
                        <b>›</b>
                      </button>
                      <button type="button" className="danger" onClick={() => go("/delete-account")}>
                        <span>
                          <strong>Delete account</strong>
                          <small>Permanently remove your account and personal data.</small>
                        </span>
                        <b>›</b>
                      </button>
                    </div>
                  )}
                </div>
              </section>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
